import { Ajax, Ajax2 } from '@shared/Ajax';
import { Notify } from '@shared/ui-native/Notify';
import { FileTreeService } from '@shared/services/FileTreeService';
import { TreeNode } from '@shared/ui-native/Tree';
import { ProjectCodesService } from '@shared/services/ProjectCodesService';
import { DateUtils } from '@shared/utils/DateUtils';

const logpointProperties = {
    timestamp: kuura.getMessage('js_logpoints.column_date'),
    code: kuura.getMessage('js_logpoints.column_code'),
    pointnumber: kuura.getMessage('js_logpoints.column_point_number'),
    northing: kuura.getMessage('js_logpoints.column_northing'),
    easting: kuura.getMessage('js_logpoints.column_easting'),
    altitude: 'Altitude',
    model: kuura.getMessage('js_logpoints.column_model_name'),
    dz: 'dz',
    source: 'Source',
    vehiclename: kuura.getMessage('js_logpoints.column_vehicle_name'),
    surfacecode: kuura.getMessage('js_logpoints.column_surfacecode'),
    linecode: kuura.getMessage('js_logpoints.column_linecode'),
    pdop: 'pdop',
    hdop: 'hdop',
    vdop: 'vdop',
    approved: 'Approved',
    alignment: kuura.getMessage('js_logpoints.column_alignment'),
    station: kuura.getMessage('js_logpoints.column_station'),
    bmeasure: kuura.getMessage('js_logpoints.column_bmeasure'),
    station_eq: kuura.getMessage('js_logpoints.column_station') + ' eq',
    pointname: kuura.getMessage('js_logpoints.column_point_name'),
    stick_height: kuura.getMessage('js_logpoints.column_stick_height'),
};

const importableProperties = {
    timestamp: kuura.getMessage('js_logpoints.column_date'),
    code: kuura.getMessage('js_logpoints.column_code'),
    surfaceCode: kuura.getMessage('js_logpoints.column_surfacecode'),
    lineCode: kuura.getMessage('js_logpoints.column_linecode'),
    x: kuura.getMessage('js_logpoints.column_northing'),
    y: kuura.getMessage('js_logpoints.column_easting'),
    z: kuura.getMessage('js_logpoints.height'),
    pdop: 'pdop',
    hdop: 'hdop',
    vdop: 'vdop',
    pointName: kuura.getMessage('js_logpoints.column_point_name'),
    pointNumber: kuura.getMessage('js_logpoints.column_point_number'),
    modelName: kuura.getMessage('js_logpoints.column_model_name'),
    referenceModelName: kuura.getMessage('js_logpoints.column_vehicle_model'),
    modelDeltaZ: 'delta z',
    type: kuura.getMessage('js_logpoints.column_source'),
    bmeasure: kuura.getMessage('js_logpoints.column_bmeasure'),
    stickHeight: kuura.getMessage('js_logpoints.column_stick_height'),
    station: kuura.getMessage('js_logpoints.column_station'),
    stationEquation: kuura.getMessage('js_logpoints.column_station') + ' eq',
    equipmentName: kuura.getMessage('js_logpoints.column_vehicle_name'),
    author: kuura.getMessage('js_logpoints.column_author'),
    approvalStage: kuura.getMessage('js_logpoints.column_stage'),
    alignment: kuura.getMessage('js_logpoints.column_alignment'),
    accuracy: kuura.getMessage('js_logpoints.accuracy'),
    fixtype: kuura.getMessage('js_logpoints.fix_type'),
};

const propertyTranslations = {
    code: kuura.getMessage('js_logpoints.column_code'),
    linecode: kuura.getMessage('js_logpoints.column_linecode'),
    surfacecode: kuura.getMessage('js_logpoints.column_surfacecode'),
    pointName: kuura.getMessage('js_logpoints.column_point_name'),
    number: kuura.getMessage('js_logpoints.column_point_number'),
    b: kuura.getMessage('tablet.sidemeasure_js'),
    date: kuura.getMessage('general.date'),
};

const colorAndDescription = {
    newer: ['red', kuura.getMessage('js_logpoints.time.newer')],
    older: ['blue', kuura.getMessage('js_logpoints.time.older')],
    in_tolerance: ['black', kuura.getMessage('js_logpoints.pdf.in_tolerance')],
    under_tolerance: ['blue', kuura.getMessage('js_logpoints.pdf.under_tolerance')],
    not_in_tolerance: ['red', kuura.getMessage('js_logpoints.pdf.over_tolerance')],
    approved: ['green', kuura.getMessage('js_logpoints.approval.approved')],
    checked: ['#FF8D2F', kuura.getMessage('js_logpoints.approval.checked')],
};

export function fetchLogpoint(logpointId: number, treeData?: any) {
    return Ajax({
        url: 'ajax_logpoint.json',
        data: {
            logpointId,
        },
    }).then(resp => {
        if (treeData && resp.ajaxLogpointData.folderId) {
            const folder = FileTreeService.findNodeById(treeData, resp.ajaxLogpointData.folderId, true);
            if (folder) {
                resp.logpointData.folderName = folder.name;
                resp.ajaxLogpointData.folderName = folder.name;
            }
        }
        return resp;
    });
}

function create(
    logpoint: Partial<ILogpoint> & {
        surfaceCode: string; // endpoint uses nonstandard naming
        pointNumber: string;
        geoid: boolean;
    }
) {
    return Ajax({
        url: '/api/1/logpoint/new.json',
        method: 'POST',
        data: {
            ...logpoint,
        },
    });
}

function download(fileType: LogpointExportFileType, ids: number[] = [], columnIds?: string[]): void {
    const idsString = ids.toString().split(' '); // Enables download of more than 10000 points
    Ajax({
        url: '/api/1/logpoint/download_by_ids.json',
        method: 'POST',
        data: {
            idsString,
            fileType,
            columnIds,
        },
    }).done(resp => {
        $('body').append("<iframe src='" + kuura.getContextPath() + resp.url + "' style='display: none;'></iframe>");
        Notify.success(kuura.getMessage('js_files.msg.notifications.success'));
    });
}

function matchesFilter(logpoint: ILogpoint, filter: Partial<ILogpointsFilter>): boolean {
    if (!filter) return true;
    if (!filter?.isReady) return false;

    // Always-AND guards: these are not multi-select fields so inter-property OR doesn't apply.
    if (filter.onlyOutOfTolerance && (logpoint.tol == 2 || logpoint.tol == 0)) return false;
    if (filter.fromTimestamp != null && logpoint.timestamp < filter.fromTimestamp) return false;
    if (filter.toTimestamp != null && logpoint.timestamp > filter.toTimestamp) return false;
    if (filter.hideDzPlusMinus != null && filter.hideDzPlusMinus > 0 && logpoint.modelDeltaZ != null) {
        if ((!filter.hideDzMode || filter.hideDzMode === 'outside') && Math.abs(logpoint.modelDeltaZ) < filter.hideDzPlusMinus) return false;
        if (filter.hideDzMode === 'inside' && Math.abs(logpoint.modelDeltaZ) > filter.hideDzPlusMinus) return false;
    }

    // Multi-select field matchers. Each returns true if the logpoint matches that field's selection.
    const fieldMatchers: Array<(lp: ILogpoint) => boolean> = [];

    if (filter.codes?.length) {
        fieldMatchers.push(lp => {
            const code = lp.code == null || lp.code === '' ? '-1' : lp.code;
            return filter.codes.some(c => c.toLowerCase() === code.toLowerCase());
        });
    }

    if (filter.vehicleIds?.length) {
        fieldMatchers.push(lp => {
            if (lp.vehicleId === null) return filter.vehicleIds.includes(-1);
            return filter.vehicleIds.includes(lp.vehicleId);
        });
    }

    if (filter.userIds?.length) {
        fieldMatchers.push(lp => {
            if (lp.creator === null) return filter.userIds.includes(-1);
            return filter.userIds.includes(lp.creator?.id);
        });
    }

    if (filter.sources?.length) {
        fieldMatchers.push(lp => filter.sources.includes(lp.type));
    }

    if (filter.approvals?.length) {
        fieldMatchers.push(lp => {
            const currStage = lp.approvalStage ?? 'MEASURED';
            return filter.approvals.includes(currStage);
        });
    }

    if (filter.surfaceCodes?.length) {
        fieldMatchers.push(lp => {
            const surfaceCode = (lp.surfacecode ?? lp.activeLayer ?? '').trim();
            return filter.surfaceCodes.includes(surfaceCode.length > 0 ? surfaceCode : '-1');
        });
    }

    if (filter.pointNames?.length) {
        fieldMatchers.push(lp => {
            const pointName = (lp.pointName ?? '').trim();
            return filter.pointNames.includes(pointName.length > 0 ? pointName : '-1');
        });
    }

    if (fieldMatchers.length === 0) return true;

    return filter.filterMode === 'or'
        ? fieldMatchers.some(match => match(logpoint)) // true at least of one filter condition => OR logic
        : fieldMatchers.every(match => match(logpoint)); // true for all filter conditions => AND logic
}

/*
 * handle null and undefined same as like they were an empty string
 * 'null' as string is necessary because <select> can't handle null value
 */
function filterMatcherEmptyValue(valA: string, valB: string) {
    if (valA == null || !valA.length || valA === 'null') {
        valA = null;
    }

    if (valB == null || !valB.length || valB === 'null') {
        valB = null;
    }

    return valA === valB;
}

function getColumnsFromPreset(preset: string, stripEmpty = false): string[] {
    const allColumns = Object.entries(importableProperties).reduce((acc, [key, value]) => {
        acc['{' + key.toLowerCase() + '}'] = value;
        return acc;
    }, {});
    const regex = new RegExp(
        '(' +
            Object.keys(allColumns)
                .map(p => `${p}`)
                .join('|') +
            '|{_})',
        'gi'
    );
    const columns = preset
        .toLowerCase()
        .split(regex)
        .filter(p => p?.trim() !== '')
        .map(c => c.replace(/[{}]/gi, ''));
    if (stripEmpty) {
        return columns.filter(c => c != '_');
    }

    return columns;
}

interface ILogpointsAjaxRequest {
    items?: number[];
    all?: boolean;
    filter: string;
}

function getModelsAndAlignmentsForProject() {
    return Ajax({ url: 'api/1/file/project_models_alignments.json' });
}

function remove(requestData: ILogpointsAjaxRequest): JQueryPromise<any> {
    const data = 'items=' + requestData.items.join(',') + '&all=' + requestData.all + '&' + requestData.filter;
    return Ajax({
        url: 'api/1/logpoint/delete.json',
        method: 'POST',
        data,
    });
}

function changeModel(requestData: ILogpointsAjaxRequest & { modelId: number }): JQueryPromise<any> {
    const data = 'items=' + requestData.items.join(',') + '&all=' + requestData.all + '&modelId=' + requestData.modelId + '&' + requestData.filter;

    return Ajax({
        url: 'api/1/logpoint/model.json',
        method: 'POST',
        data,
    });
}

function changeFolder(requestData: ILogpointsAjaxRequest & { folderId: number }): JQueryPromise<any> {
    const data = 'items=' + requestData.items.join(',') + '&all=' + requestData.all + '&newFolderId=' + requestData.folderId + '&' + requestData.filter;

    return Ajax({
        url: 'api/1/logpoint/move.json',
        method: 'POST',
        data,
    });
}

function changeAlignment(requestData: ILogpointsAjaxRequest & { alignmentId: number }): JQueryPromise<any> {
    const data = 'items=' + requestData.items.join(',') + '&all=' + requestData.all + '&alignmentId=' + requestData.alignmentId + '&' + requestData.filter;

    return Ajax({
        url: 'api/1/logpoint/alignment.json',
        method: 'POST',
        data,
    });
}

function changeVehicle(requestData: ILogpointsAjaxRequest & { vehicleId: number }): JQueryPromise<any> {
    const data = 'items=' + requestData.items.join(',') + '&all=' + requestData.all + '&vehicleId=' + requestData.vehicleId + '&' + requestData.filter;

    return Ajax({
        url: '/api/1/logpoint/vehicle.json',
        method: 'POST',
        data,
    });
}

function changeSurfaceCode(requestData: ILogpointsAjaxRequest & { surfaceCode: string }): JQueryPromise<any> {
    const data = 'items=' + requestData.items.join(',') + '&all=' + requestData.all + '&newSurfaceCode=' + requestData.surfaceCode + '&' + requestData.filter;

    return Ajax({
        url: 'api/1/logpoint/update_string_field.json',
        method: 'POST',
        data,
    });
}

function changeLineCode(requestData: ILogpointsAjaxRequest & { lineCode: string }): JQueryPromise<any> {
    const data = 'items=' + requestData.items.join(',') + '&all=' + requestData.all + '&newLineCode=' + requestData.lineCode + '&' + requestData.filter;

    return Ajax({
        url: 'api/1/logpoint/update_string_field.json',
        method: 'POST',
        data,
    });
}

function changeCode(requestData: ILogpointsAjaxRequest & { code: string }): JQueryPromise<any> {
    const data = 'items=' + requestData.items.join(',') + '&all=' + requestData.all + '&newCode=' + requestData.code + '&' + requestData.filter;

    return Ajax({
        url: 'api/1/logpoint/update_string_field.json',
        method: 'POST',
        data,
    });
}

function changePointName(requestData: ILogpointsAjaxRequest & { pointName: string }): JQueryPromise<any> {
    const data = 'items=' + requestData.items.join(',') + '&all=' + requestData.all + '&newPointName=' + requestData.pointName + '&' + requestData.filter;

    return Ajax({
        url: 'api/1/logpoint/update_string_field.json',
        method: 'POST',
        data,
    });
}

function changePointNumber(requestData: ILogpointsAjaxRequest & { pointNumber: string }): JQueryPromise<any> {
    const data = 'items=' + requestData.items.join(',') + '&all=' + requestData.all + '&newPointNumber=' + requestData.pointNumber + '&' + requestData.filter;

    return Ajax({
        url: 'api/1/logpoint/update_string_field.json',
        method: 'POST',
        data,
    });
}

function getChangesHistory(logpointId: number): JQueryPromise<any> {
    return Ajax({
        url: `/api/1/logpoint/${logpointId}/action_history.json`,
    });
}

function getApprovalHistory(logpointId: number): JQueryPromise<any> {
    return Ajax({
        url: '/ajax_logpoint.json',
        data: {
            logpointId,
        },
    });
}

function fetchFolderCounts(folderIdsArray: string[] = []): Promise<{
    childLogpointsExist: object;
    folderCounts: object;
    logpointsExistInSubFilesOrSubFolders: object;
    modelCounts: object;
    totalCount: number;
}> {
    const folderIds = folderIdsArray.join(',');

    return Ajax2({
        url: kuura.getContextPath() + '/api/1/logpoint/counts_for_folders_and_models.json',
        method: 'POST',
        data: {
            folderIds,
            projectId: kuura.getProjectId(),
        },
    });
}

function renderFolderCounts(tree: TreeNode, childLogpointsExists, totalFolderCounts, totalModelCounts, filteredFolderCounts?, filteredModelCounts?): void {
    tree.each(
        child => {
            const childLogpointsExist = childLogpointsExists[child.id];
            let text = '';

            if (child.isFolder()) {
                if (totalFolderCounts[child.id] > 0) {
                    if (filteredFolderCounts) {
                        text += `(${filteredFolderCounts[child.id] ?? 0}/${totalFolderCounts[child.id]})`;
                    } else {
                        text += `(${totalFolderCounts[child.id]})`;
                    }
                }
                if (childLogpointsExist == 1) {
                    text += ` (...)`;
                }
            } else {
                if (totalModelCounts[child.id] > 0) {
                    if (filteredFolderCounts) {
                        text += `(${filteredModelCounts[child.id] ?? 0}/${totalModelCounts[child.id]})`;
                    } else {
                        text += `(${totalModelCounts[child.id]})`;
                    }
                }
            }

            if (text) {
                child.setText(`${child.ajaxData.name} <span style="color: #B1B1B1">${text}</span>`);
            } else {
                child.setText(child.ajaxData.name);
            }
        },
        true,
        true
    );
}

function getLabelHtmlForLogpoint(point: ILogpoint): string {
    const content = [];
    content.push("<div class='json-pairs'>");

    if (point.code) {
        content.push('<div>');
        content.push(kuura.getMessage('tablet.code_js'));
        content.push(': ');
        content.push(ProjectCodesService.codeWithDescription(point.code));
        content.push('</div>');
    }

    if (point.number) {
        content.push('<div>');
        content.push(kuura.getMessage('tablet.number_js'));
        content.push(': ');
        content.push(point.number);
        content.push('</div>');
    }

    content.push(kuura.getMessage('general.upload_date'));
    content.push(': ');
    content.push(DateUtils.formatDateTime(point.timestamp));
    content.push('</div>');

    return content.join('');
}

export const LogpointsService = {
    fetchLogpoint,
    create,
    download,
    matchesFilter,
    propertyTranslations,
    colorAndDescription,
    logpointProperties,
    importableProperties,
    importExportPresetsStorageKey: 'LOGPOINT_IMPORT_EXPORT_PRESETS',
    getColumnsFromPreset,
    filetreeFilterModels(c) {
        if (c.tolerance) return true; // show folders
        return kuuraModel.isModel(c);
    },
    filetreeFilterAlignments(item) {
        if (item.tolerance) return true; // folder
        return kuuraModel.isAlignment(item);
    },
    remove,
    changeModel,
    changeFolder,
    changeAlignment,
    changeVehicle,
    changeSurfaceCode,
    changeLineCode,
    changeCode,
    changePointName,
    changePointNumber,
    getChangesHistory,
    getApprovalHistory,

    fetchFolderCounts,
    renderFolderCounts,

    getLabelHtmlForLogpoint,
};
